import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { CheckCircle2, QrCode, Smartphone, Settings, MoreVertical, Loader2, Link as LinkIcon, ExternalLink, ShieldCheck, Share2, Copy, Check, Info } from "lucide-react";
import useAuth from "../../../components/hooks/useAuth";
import { Helmet } from "react-helmet";
import useDeviceFingerprint from "../../../components/deviceFingerPrint";


// Definimos uma constante para a logo
const notifylog = "/assets/notifylog.png";

/**
 * COMPONENTE: Sucesso na Conexão (Estado Conectado)
 */
const WhatsAppActComponentQrCodeReadedShow = ({ linkedZap }) => {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px 0',
      textAlign: 'center',
      animation: 'fadeInScale 0.4s ease-out'
    }}>
      <div style={{
        width: 100,
        height: 100,
        backgroundColor: '#e7fce3',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 24,
        boxShadow: '0 4px 15px rgba(0, 168, 132, 0.1)'
      }}>
        <CheckCircle2 size={56} color="#00a884" />
      </div>

      <h2 style={{ fontSize: 26, fontWeight: '700', color: '#111b21', marginBottom: 8 }}>
        Conexão Concluída!
      </h2>

      <p style={{ color: '#667781', marginBottom: 32, fontSize: 16 }}>
        A conta <strong>{linkedZap.name}</strong> está ativa.
      </p>

      <div style={{
        width: '100%',
        maxWidth: 380,
        backgroundColor: '#f8f9fa',
        border: '1px solid #e9edef',
        borderRadius: 16,
        padding: 20,
        marginBottom: 32,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        position: 'relative'
      }}>
        <span style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '1px', color: '#8696a0', fontWeight: '800', marginBottom: 8 }}>Dados Técnicos</span>
        <span style={{ fontSize: 16, fontFamily: 'monospace', fontWeight: '600', color: '#3b4a54', wordBreak: 'break-all' }}>
          {linkedZap.whatsapp_id?.split('@')[0] || "Identificado"}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, color: '#00a884', fontSize: 14, fontWeight: '600' }}>
          <ShieldCheck size={16} /> Verificado e Seguro
        </div>
      </div>

      <button
        onClick={() => (window.location.href = "/app")}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '14px 40px',
          backgroundColor: '#00a884',
          color: '#ffffff',
          fontWeight: '700',
          fontSize: 16,
          borderRadius: 30,
          border: 'none',
          cursor: 'pointer',
          boxShadow: '0 4px 12px rgba(0, 168, 132, 0.25)',
          transition: 'transform 0.2s ease'
        }}
        onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
      >
        Ir para o Dashboard
        <ExternalLink size={18} />
      </button>
    </div>
  );
};

/**
 * COMPONENTE: QR Code e Instruções (Estado Desconectado)
 */
const WhatsAppActComponentQrCodeShow = ({ linkedZap, handleCopiarLink, linkCopiado }) => {
  if (!linkedZap.id) return (
    <div style={{ padding: 100, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>
      <Loader2 size={48} color="#00a884" className="animate-spin" />
      <span style={{ color: '#8696a0', fontWeight: '500' }}>Sincronizando com servidor...</span>
    </div>
  );

  return (
    <div style={{
      display: 'flex',
      flexDirection: window.innerWidth < 850 ? 'column' : 'row',
      gap: 50,
      alignItems: 'flex-start',
      width: '100%',
      animation: 'fadeInUp 0.6s ease-out'
    }}>
      {/* Coluna de Instruções */}
      <div style={{ flex: 1.2, display: 'flex', flexDirection: 'column', gap: 24 }}>
        <div style={{ textAlign: 'left' }}>
          <div style={{ color: '#8696a0', fontSize: 13, textTransform: 'uppercase', fontWeight: '700', marginBottom: 4 }}>Instruções de Acesso</div>
          <h3 style={{ color: '#3b4a54', fontSize: 22, margin: 0, fontWeight: '400' }}>
            Vincular dispositivo: <strong style={{ color: '#00a884', fontWeight: '600' }}>{linkedZap.name}</strong>
          </h3>
        </div>

        <ol style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 18 }}>
          {[
            { icon: <Smartphone size={18} />, text: <>Abra o <b>WhatsApp</b> no seu celular</> },
            { icon: <div style={{ display: 'flex', gap: 2 }}><MoreVertical size={14} /><Settings size={14} /></div>, text: <>Vá em <b>Aparelhos conectados</b></> },
            { icon: <LinkIcon size={18} />, text: <>Toque em <b>Conectar um dispositivo</b></> },
            { icon: <QrCode size={18} />, text: <>Aponte a câmera para este código</> },
          ].map((item, index) => (
            <li key={index} style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
              <div style={{
                flexShrink: 0,
                width: 28,
                height: 28,
                borderRadius: '50%',
                backgroundColor: '#00a88415',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#00a884',
                fontSize: 14,
                fontWeight: '700'
              }}>
                {index + 1}
              </div>
              <div style={{ paddingTop: 4 }}>
                <div style={{ fontSize: 15, lineHeight: 1.4, color: '#3b4a54' }}>{item.text}</div>
              </div>
            </li>
          ))}
        </ol>

        {/* SEÇÃO DE COMPARTILHAMENTO INTUITIVA */}
        <div style={{
          marginTop: 10,
          padding: '20px',
          backgroundColor: '#f8fafc',
          borderRadius: '12px',
          border: '1px solid #e2e8f0',
          display: 'flex',
          flexDirection: 'column',
          gap: 12
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#475569' }}>
            <Share2 size={16} color="#00a884" />
            <span style={{ fontSize: 14, fontWeight: '600' }}>Outra pessoa vai conectar?</span>
          </div>
          <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>
            Copie o link seguro abaixo e envie para quem for realizar o escaneamento.
          </p>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            backgroundColor: '#ffffff',
            border: linkCopiado ? '1px solid #00a884' : '1px solid #cbd5e1',
            borderRadius: '8px',
            padding: '4px',
            transition: 'all 0.3s ease'
          }}>
            <div style={{
              flex: 1,
              fontSize: 12,
              color: '#94a3b8',
              padding: '0 12px',
              whiteSpace: 'break-spaces',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
              {window.location.href}
            </div>
            <button
              onClick={handleCopiarLink}
              style={{
                backgroundColor: linkCopiado ? '#00a884' : '#f1f5f9',
                color: linkCopiado ? '#ffffff' : '#475569',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '6px',
                fontSize: 13,
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                transition: 'all 0.2s ease'
              }}
            >
              {linkCopiado ? <><Check size={14} /> Copiado</> : <><Copy size={14} /> Copiar Link</>}
            </button>
          </div>
        </div>
      </div>

      {/* Coluna do QR Code */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 0.8, width: window.innerWidth < 850 ? '100%' : 'auto' }}>
        <div style={{
          width: 280,
          height: 280,
          backgroundColor: '#ffffff',
          padding: 12,
          borderRadius: 20,
          border: '1px solid #e9edef',
          boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden'
        }}>

          {!linkedZap.connected && !linkedZap.qrcode_text && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
              <Loader2 size={32} color="#00a884" className="animate-spin" />
              <span style={{ fontSize: 11, color: '#8696a0', fontWeight: '700', textTransform: 'uppercase' }}>Gerando Código...</span>
            </div>
          )}

          {!linkedZap.connected && linkedZap.qrcode_text && (
            <div style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <img
                src={linkedZap.qrcode_text}
                alt="QR Code"
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
              <div className="qr-scan-line" />
            </div>
          )}
        </div>

        <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '8px 24px',
            backgroundColor: '#fff8f1',
            color: '#e67e22',
            borderRadius: 30,
            fontSize: 14,
            fontWeight: '700',
            border: '1px solid #ffe8d4'
          }}>
            <span className="dot-pulse-orange" />
            Aguardando escaneamento
          </div>

          {linkedZap.qrcode_updated_at && (
            <span style={{ fontSize: 11, color: '#aebac1', fontWeight: '500' }}>
              Sincronizado às {new Date(linkedZap.qrcode_updated_at).toLocaleTimeString()}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * COMPONENTE PRINCIPAL
 */
const WhatsAppActComponentShow = () => {
  const { id } = useParams();
  const [linkedZap, setLinkedZap] = useState([]);
  const [linkCopiado, setLinkCopiado] = useState(false);
  const [isLoadingInternal, setIsLoadingInternal] = useState(false);

  const { getApi, postApi } = useAuth();

  useEffect(() => {

    const intervalId = setInterval(() => {
      if (!isLoadingInternal) {
        loadLinkedZap();
      }
    }, 5000);

    return () => clearInterval(intervalId);
  }, [isLoadingInternal]);

  useEffect(() => {
    loadLinkedZap();
  }, [id, linkedZap.connected]);

  const loadLinkedZap = async () => {
    setIsLoadingInternal(true);

    try {
      let response = await getApi("/api/v1/linkedZap/show/public/" + id);
      setLinkedZap(response);
    } catch (error) {
      console.error("Falha na sincronização:", error);
    } finally {
      setIsLoadingInternal(false);

    }

  };

  const handleCopiarLink = () => {
    // Método de cópia robusto
    const el = document.createElement('textarea');
    el.value = window.location.href;
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);

    setLinkCopiado(true);
    setTimeout(() => setLinkCopiado(false), 3000);
  };

  const navigate = useNavigate();
  const deviceID = useDeviceFingerprint();

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f0f2f5',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
      position: 'relative',
      overflowX: 'hidden'
    }}>
      <Helmet>
        <title>{`${linkedZap.name || 'Carregando...'} - Notify Flow`}</title>
      </Helmet>
      {/* Top Bar Background */}
      <div style={{ width: '100%', height: 220, backgroundColor: '#00a884', position: 'absolute', top: 0, left: 0, zIndex: 0 }} />

      <div style={{ zIndex: 1, marginTop: 40, marginBottom: 40, width: '100%', maxWidth: 1000, padding: '0 20px' }}>

        {/* HEADER COM LOGO NOTIFY FLOW */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 35 }}>
          <div style={{
            backgroundColor: 'rgba(255,255,255,0.15)',
            padding: '10px 18px',
            borderRadius: 12,
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(255,255,255,0.3)',
            display: 'flex',
            alignItems: 'center',
            gap: 12
          }}>
            <img
              src={notifylog}
              alt="Notify Flow"
              style={{ height: 32, width: 'auto', objectFit: 'contain' }}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
            <div style={{ height: 24, width: 1, backgroundColor: 'rgba(255,255,255,0.4)' }} />
            <h1 style={{ color: '#ffffff', fontWeight: '600', fontSize: 18, margin: 0, letterSpacing: '0.5px' }}>
              Notify Flow <span style={{ fontWeight: '300', opacity: 0.8 }}>| Conexão</span>
            </h1>
          </div>
        </div>

        {/* Card Principal */}
        <main style={{
          backgroundColor: '#ffffff',
          borderRadius: 8,
          boxShadow: '0 17px 50px 0 rgba(0,0,0,0.19), 0 12px 15px 0 rgba(0,0,0,0.24)',
          overflow: 'hidden',
          minHeight: 580,
          display: 'flex',
          flexDirection: 'column',
          position: 'relative'
        }}>
          {/* Badge Lateral Decorativa */}
          <div style={{ position: 'absolute', top: 0, right: 0, width: 4, height: '100%', backgroundColor: '#00a884' }} />

          <div style={{ padding: window.innerWidth < 600 ? '40px 24px' : '60px 70px', flex: 1, display: 'flex', flexDirection: 'column' }}>

            {/* Título Interno */}
            <div style={{ width: '100%', textAlign: 'left', marginBottom: 50, borderBottom: '1px solid #f0f2f5', paddingBottom: 25 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                <div>
                  <h2 style={{ fontSize: 32, fontWeight: '300', color: '#54656f', margin: 0 }}>
                    Vincular WhatsApp
                  </h2>
                  <div style={{ height: 4, width: 70, backgroundColor: '#00a884', marginTop: 12, borderRadius: 2 }} />
                </div>
                {linkedZap.name && (
                  <div style={{ textAlign: 'right', display: window.innerWidth < 600 ? 'none' : 'block' }}>
                    <div style={{ color: '#8696a0', fontSize: 11, fontWeight: '700', textTransform: 'uppercase' }}>Configurando Conta</div>
                    <div style={{ color: '#00a884', fontSize: 18, fontWeight: '600' }}>{linkedZap.name}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Alternância de Estados baseada no JSON do linkedZap */}
            {(linkedZap && linkedZap.id && linkedZap.connected) ? (
              <WhatsAppActComponentQrCodeReadedShow linkedZap={linkedZap} />
            ) : (
              <WhatsAppActComponentQrCodeShow
                linkedZap={linkedZap}
                handleCopiarLink={handleCopiarLink}
                linkCopiado={linkCopiado}
              />
            )}
          </div>

          {/* Rodapé Interno */}
          <footer style={{ backgroundColor: '#f9fafb', borderTop: '1px solid #f0f2f5', padding: '20px 40px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <p style={{ fontSize: 12, color: '#8696a0', display: 'flex', alignItems: 'center', gap: 6, margin: 0 }}>
              🔒 Criptografia SSL Ativa
            </p>
            <p style={{ fontSize: 12, color: '#8696a0', display: 'flex', alignItems: 'center', gap: 6, margin: 0 }}>
              Sessão: {deviceID || "Gerando..."}
            </p>
          </footer>
        </main>
      </div>

      {/* Estilos Globais de Animação */}
      <style>
        {`
          @keyframes fadeInUp {
            from { opacity: 0; transform: translateY(30px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes fadeInScale {
            from { opacity: 0; transform: scale(0.92); }
            to { opacity: 1; transform: scale(1); }
          }
          @keyframes scanLine {
            0% { top: 0; }
            50% { top: 100%; }
            100% { top: 0; }
          }
          @keyframes pulse {
            0% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.3); opacity: 0.5; }
            100% { transform: scale(1); opacity: 1; }
          }
          .animate-spin {
            animation: spin 1.2s linear infinite;
          }
          @keyframes spin {
            from { transform: rotate(0deg); } to { transform: rotate(360deg); }
          }
          .qr-scan-line {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 3px;
            background: linear-gradient(to right, transparent, #00a884, transparent);
            box-shadow: 0 0 12px #00a884;
            animation: scanLine 4s ease-in-out infinite;
            opacity: 0.7;
            z-index: 10;
          }
          .dot-pulse {
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background-color: #00a884;
            animation: pulse 2s infinite ease-in-out;
          }
          .dot-pulse-orange {
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background-color: #e67e22;
            animation: pulse 1.8s infinite ease-in-out;
          }
        `}
      </style>
    </div>
  );
};

export default WhatsAppActComponentShow;